import {
  formatCurrency,
  getAlertSeverityColor,
  getToleranceColor,
  getSignificanceColor,
} from '@/util/fcc/FCCUtil'
import { formatDateTimeForDisplay } from '@/util/dateUtils'

// FCC keeps its own local-timezone date formatter — deliberately NOT the
// UTC-normalized util formatDate. Do not swap this for @/util/dateUtils.
const formatDate = (date: Date | string | null): string => {
  if (!date) return ''
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return ''
  return dateObj.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const toFiniteNumber = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// Display primitives — single source of truth for each delimiter/decimal rule.
const formatRate = (value: number): string =>
  `${(Math.round(value * 10000) / 100).toFixed(2)}%`

const formatCount = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 0 })

const formatDecimal = (value: number): string => value.toFixed(2)

const formatCurrencyOrNA = (
  value: number | string | null | undefined,
): string => {
  const parsed = toFiniteNumber(value)
  return parsed === null ? 'N/A' : formatCurrency(parsed)
}

export const useFCCFormatting = () => {
  // Alerts table — RATE→%, COUNT/AVG→decimal, VOLUME and unknown→currency.
  const formatAlertType = (value: number, alertType: string): string => {
    if (alertType.includes('RATE')) return formatRate(value)
    if (alertType.includes('COUNT')) return formatDecimal(value)
    if (alertType.includes('AVG')) return formatDecimal(value)
    if (alertType.includes('VOLUME')) return formatCurrency(value)
    return formatCurrency(value)
  }

  // Merchant Key Metrics — RATE→%, currency-bearing keys→$, COUNT→integer, else decimal.
  // Keys arrive in mixed shapes (SCREAMING_SNAKE_CASE or "Title Case") — normalize before matching.
  const formatKeyMetric = (key: string, value: string | number): string => {
    if (typeof value !== 'number') return value
    const normalized = key.toUpperCase().replace(/\s+/g, '_')
    if (normalized.includes('RATE')) return formatRate(value)
    if (
      normalized.includes('VOLUME') ||
      normalized.includes('REFUNDS') ||
      normalized.includes('AVG_TRANSACTION_SIZE')
    ) {
      return formatCurrency(value)
    }
    if (normalized.includes('COUNT')) return formatCount(value)
    return formatDecimal(value)
  }

  const formatMaxAmount = formatCurrencyOrNA

  // Title casing plus two patches: the API's EXCPECTED typo, and BIN (Bank
  // Identification Number), which would otherwise read as the word "bin".
  const formatEventType = (eventType: string): string => {
    return eventType
      .replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('High Excpected Daily Volume', 'High Expected Daily Volume')
      .replace('Low Excpected Daily Volume', 'Low Expected Daily Volume')
      .replace(/\bBin\b/g, 'BIN')
      .replace(/_/g, ' ')
  }

  const formatPlatform = (platform: string | undefined | null): string => {
    if (!platform || typeof platform !== 'string') {
      return 'Unknown'
    }
    return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase()
  }

  const formatTolerance = (tolerance: string | undefined | null): string => {
    if (!tolerance || typeof tolerance !== 'string') {
      return 'Unknown'
    }
    return tolerance.charAt(0).toUpperCase() + tolerance.slice(1).toLowerCase()
  }

  // Significance is the same kind of dollar amount as the alerts table's Statistic column, so it
  // shares that column's formatter. Do not reintroduce K/M abbreviation here: the two columns sit
  // side by side, and an abbreviated "$34.9K" next to a full "$41,727.42" reads as a different unit.
  // '$0' (not '$0.00') stays the invalid-value fallback so it is distinguishable from a real zero.
  const formatSignificance = (significance: number): string => {
    if (typeof significance !== 'number' || isNaN(significance)) {
      return '$0'
    }

    return formatCurrency(significance)
  }

  const formatRelativeTime = (
    value: string | number | Date | null | undefined,
  ): string => {
    if (value === null || value === undefined || value === '') return ''
    const then = value instanceof Date ? value.getTime() : new Date(value).getTime()
    if (!Number.isFinite(then)) return ''
    const diffMs = Date.now() - then
    const future = diffMs < 0
    const seconds = Math.floor(Math.abs(diffMs) / 1000)
    if (seconds < 5) return 'just now'
    if (seconds < 60) return `${seconds}s ${future ? 'from now' : 'ago'}`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ${future ? 'from now' : 'ago'}`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ${future ? 'from now' : 'ago'}`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ${future ? 'from now' : 'ago'}`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months}mo ${future ? 'from now' : 'ago'}`
    const years = Math.floor(days / 365)
    return `${years}y ${future ? 'from now' : 'ago'}`
  }

  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = formatDate(startDate)
    const end = formatDate(endDate)
    return `${start} - ${end}`
  }

  return {
    formatCurrency,
    formatCurrencyOrNA,
    formatRate,
    formatCount,
    formatDecimal,
    formatDate,
    formatDateTimeForDisplay,
    formatAlertType,
    formatKeyMetric,
    formatMaxAmount,
    formatEventType,
    formatPlatform,
    formatTolerance,
    formatSignificance,
    formatRelativeTime,
    formatDateRange,
    // Color helpers
    getAlertSeverityColor,
    getToleranceColor,
    getSignificanceColor,
  }
}
